import React from "react";

const AdminTradingPanel = ({
  currentUser,
  autonomousStatus,
  autonomousLoading,
  startAutonomousTrading,
  stopAutonomousTrading,
  emergencyStopTrading,
  fetchAutonomousStatus,
}) => {
  const isAdmin = currentUser?.is_admin || currentUser?.role === 'super_admin' || currentUser?.admin_role === 'super_admin';
  if (!isAdmin) return null;

  return (
    <div className="bg-gray-800 rounded-lg shadow-lg p-6 border border-gray-700">
      <h3 className="text-xl font-semibold text-white mb-4 flex items-center">
        <span className="mr-2">Autonomous Trading System</span>
        {autonomousStatus.is_running && (
          <span className="ml-2 px-2 py-1 bg-green-600 text-white text-xs rounded-full">ACTIVE</span>
        )}
      </h3>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
        <div className="bg-gray-700 p-4 rounded-lg">
          <div className="text-sm text-gray-400">Status</div>
          <div className="text-lg font-semibold text-white">
            {autonomousStatus.is_running
              ? <span className="text-green-400">Running</span>
              : <span className="text-gray-400">Stopped</span>}
          </div>
          <div className="text-xs text-gray-500">
            Mode: {autonomousStatus.paper_trading ? 'Test Mode' : 'Live Trading'}
          </div>
        </div>
        <div className="bg-gray-700 p-4 rounded-lg">
          <div className="text-sm text-gray-400">Session Stats</div>
          <div className="text-lg font-semibold text-white">
            {autonomousStatus.trades_executed || 0} Trades
          </div>
          <div className="text-xs text-gray-500">
            Session: {autonomousStatus.session_duration_hours?.toFixed(1) || 0}h
          </div>
        </div>
        <div className="bg-gray-700 p-4 rounded-lg">
          <div className="text-sm text-gray-400">System Health</div>
          <div className="text-lg font-semibold text-white">
            {autonomousStatus.system_health?.components_initialized
              ? <span className="text-green-400">Ready</span>
              : <span className="text-yellow-400">Initializing</span>}
          </div>
          <div className="text-xs text-gray-500">
            AI Advisor: {autonomousStatus.system_health?.ai_advisor_ready ? 'Active' : 'Offline'}
          </div>
          <div className="text-xs text-gray-500">
            Databento: {autonomousStatus.system_health?.databento_connected ? 'Connected' : 'Disconnected'}
          </div>
        </div>
      </div>

      {autonomousStatus.ai_advisor && (
        <div className="bg-gray-700 p-4 rounded-lg mb-4">
          <div className="text-sm text-gray-400 mb-2">Enhanced AI Trade Advisor v2.0</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div><span className="text-gray-400">Type:</span> <span className="ml-1 text-white">Enhanced Engine</span></div>
            <div><span className="text-gray-400">Intelligence:</span> <span className="ml-1 text-green-400">Very High</span></div>
            <div><span className="text-gray-400">Features:</span> <span className="ml-1 text-white">9+ Analysis Modules</span></div>
            <div><span className="text-gray-400">Performance:</span> <span className="ml-1 text-green-400">Sub-millisecond</span></div>
          </div>
          <div className="mt-2 text-xs text-gray-500">
            Features: Symbol Quality (3-tier), Volume Microstructure, Volatility Regimes, 
            Price Momentum, Market Timing, Statistical Analysis, Risk Aggregation
          </div>
        </div>
      )}

      {autonomousStatus.guardrails && (
        <div className="bg-gray-700 p-4 rounded-lg mb-4">
          <div className="text-sm text-gray-400 mb-2">Trading Guardrails</div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-xs">
            <div>
              <span className="text-gray-400">Daily Trades:</span>
              <span className="ml-1 text-white">
                {autonomousStatus.guardrails.daily_trade_count || 0}/{autonomousStatus.guardrails.max_daily_trades || 20}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Consecutive Losses:</span>
              <span className="ml-1 text-white">
                {autonomousStatus.guardrails.consecutive_losses || 0}/{autonomousStatus.guardrails.max_consecutive_losses || 5}
              </span>
            </div>
            <div>
              <span className="text-gray-400">Session Hours:</span>
              <span className="ml-1 text-white">
                {autonomousStatus.guardrails.session_duration_hours || 0}/{autonomousStatus.guardrails.max_session_duration_hours || 8}h
              </span>
            </div>
            <div>
              <span className="text-gray-400">Emergency Stop:</span>
              <span className={`ml-1 ${autonomousStatus.guardrails.emergency_stop_active ? 'text-red-400' : 'text-green-400'}`}>
                {autonomousStatus.guardrails.emergency_stop_active ? 'Active' : 'Clear'}
              </span>
            </div>
          </div>
        </div>
      )}

      <div className="flex flex-wrap gap-2">
        {!autonomousStatus.is_running ? (
          <>
            <button
              onClick={() => startAutonomousTrading(true)}
              disabled={autonomousLoading}
              className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {autonomousLoading ? (
                <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>Starting...</>
              ) : (
                <>Start Test Trading</>
              )}
            </button>
            <button
              onClick={() => startAutonomousTrading(false)}
              disabled={autonomousLoading}
              className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
            >
              {autonomousLoading ? (
                <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>Starting...</>
              ) : (
                <>Start Live Trading</>
              )}
            </button>
          </>
        ) : (
          <button
            onClick={stopAutonomousTrading}
            disabled={autonomousLoading}
            className="bg-yellow-600 hover:bg-yellow-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
          >
            {autonomousLoading ? (
              <><div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2"></div>Stopping...</>
            ) : (
              <>Stop Trading</>
            )}
          </button>
        )}
        <button
          onClick={() => emergencyStopTrading("Manual emergency stop from dashboard")}
          disabled={autonomousLoading}
          className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          Emergency Stop
        </button>
        <button
          onClick={fetchAutonomousStatus}
          disabled={autonomousLoading}
          className="bg-gray-600 hover:bg-gray-700 text-white px-4 py-2 rounded-lg disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
        >
          Refresh Status
        </button>
      </div>

      <div className="mt-4 text-xs text-gray-500">
        Autonomous trading requires admin privileges. Always test with small amounts first.
      </div>
    </div>
  );
};

export default AdminTradingPanel;
